import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, KeyRound, Mail, Hash, Calendar, Globe, Store,
  LogIn, UserPlus, Utensils, CreditCard, ShieldAlert, Activity, Ban, CheckCircle2, PauseCircle, PlayCircle, ExternalLink, Trash2, SlidersHorizontal, CalendarClock,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const PROTECTED_EMAILS = new Set(["info@kouzinahospitality.com", "info@wshpl.com"]);

const EVENT_META = {
  account_created: { icon: UserPlus, color: "text-brand-orange", bg: "bg-brand-orange/10" },
  login: { icon: LogIn, color: "text-brand-navy", bg: "bg-brand-navy/10" },
  menu_item_created: { icon: Utensils, color: "text-veg", bg: "bg-veg/10" },
  menu_item_updated: { icon: Utensils, color: "text-brand-navy", bg: "bg-brand-navy/10" },
  menu_item_deleted: { icon: Utensils, color: "text-nonveg", bg: "bg-nonveg/10" },
  subscription_changed: { icon: CreditCard, color: "text-brand-orange", bg: "bg-brand-orange/10" },
  password_reset_by_admin: { icon: ShieldAlert, color: "text-amber-600", bg: "bg-amber-500/10" },
};

function eventMeta(type) {
  return EVENT_META[type] || { icon: Activity, color: "text-foreground/60", bg: "bg-muted" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function InfoRow({ icon: Icon, label, value, mono, action }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-foreground/60" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
        <div className={`text-sm mt-0.5 break-all ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
      </div>
      {action}
    </div>
  );
}

function ManageAccessDialog({ email, subscription, onSave }) {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(subscription?.outlet_limit && !subscription?.unlimited ? subscription.outlet_limit : 1);
  const [unlimited, setUnlimited] = useState(!!subscription?.unlimited);
  const [saving, setSaving] = useState(false);

  // Resync when the dialog opens (picks up latest sub if admin updated elsewhere)
  const handleOpen = (v) => {
    if (v) {
      setUnlimited(!!subscription?.unlimited);
      setLimit(subscription?.outlet_limit && !subscription?.unlimited ? subscription.outlet_limit : 1);
    }
    setOpen(v);
  };

  const submit = async () => {
    setSaving(true);
    const ok = await onSave({ outlet_limit: unlimited ? 9999 : Number(limit) || 1, unlimited });
    setSaving(false);
    if (ok) setOpen(false);
  };

  const currentSummary = subscription
    ? subscription.unlimited
      ? "Unlimited outlets"
      : `${subscription.outlet_limit} outlet${subscription.outlet_limit === 1 ? "" : "s"} · plan ${subscription.plan}`
    : "No active plan";

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-brand-orange text-brand-orange hover:bg-brand-orange/10" data-testid="manage-access-btn">
          <SlidersHorizontal className="w-4 h-4" /> Manage access
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="manage-access-dialog">
        <DialogHeader>
          <DialogTitle>Manage outlet access</DialogTitle>
          <DialogDescription>
            Set how many outlets <strong className="break-all">{email}</strong> can run. This replaces any active
            restaurant plan (10-year validity, ₹0). Event/Party packs are unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="text-xs text-muted-foreground">
            <span className="uppercase tracking-[0.15em] font-bold text-foreground/70">Current:</span> {currentSummary}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div>
              <div className="text-sm font-semibold">Unlimited outlets</div>
              <div className="text-xs text-muted-foreground">Partner / white-glove accounts</div>
            </div>
            <Switch checked={unlimited} onCheckedChange={setUnlimited} data-testid="access-unlimited-switch" />
          </div>

          <div className={unlimited ? "opacity-40 pointer-events-none" : ""}>
            <Label htmlFor="outlet-limit" className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Outlet limit</Label>
            <Input
              id="outlet-limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="mt-1.5"
              data-testid="access-limit-input"
              disabled={unlimited}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {[1, 2, 5, 10, 25].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setLimit(n)}
                  disabled={unlimited}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                    Number(limit) === n && !unlimited
                      ? "border-brand-orange bg-brand-orange/10 text-brand-orange"
                      : "border-border text-foreground/70 hover:border-brand-orange/60"
                  }`}
                  data-testid={`access-preset-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand-orange hover:bg-[#E65C00] text-white" data-testid="save-access-btn">
            {saving ? "Saving…" : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtendValidityDialog({ email, subscription, onSave }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    const ok = await onSave({ days: Number(days) || 30 });
    setSaving(false);
    if (ok) setOpen(false);
  };

  const currentEnd = subscription?.ends_at
    ? new Date(subscription.ends_at).toLocaleDateString(undefined, { dateStyle: "medium" })
    : "—";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-brand-navy text-brand-navy hover:bg-brand-navy/10" data-testid="extend-validity-btn">
          <CalendarClock className="w-4 h-4" /> Extend validity
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="extend-validity-dialog">
        <DialogHeader>
          <DialogTitle>Extend subscription validity</DialogTitle>
          <DialogDescription>
            Adds N days to <strong className="break-all">{email}</strong>&rsquo;s active subscription. Current expiry: <strong>{currentEnd}</strong>.
            If the plan had already expired, it&rsquo;s re-activated and the new expiry is <code className="font-mono text-xs">today + N days</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div>
            <Label htmlFor="extend-days" className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Days to add</Label>
            <Input
              id="extend-days"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1.5"
              data-testid="extend-days-input"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {[7, 15, 30, 90, 365].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDays(n)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                    Number(days) === n
                      ? "border-brand-orange bg-brand-orange/10 text-brand-orange"
                      : "border-border text-foreground/70 hover:border-brand-orange/60"
                  }`}
                  data-testid={`extend-preset-${n}`}
                >
                  +{n}d
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !days || days < 1} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid="save-extend-btn">
            {saving ? "Saving…" : `Extend by ${days || 0} days`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/users/${id}`),
        api.get(`/admin/users/${id}/activity`),
      ]);
      setData(d.data); setActivity(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action) => {
    try {
      await api.post(`/admin/users/${id}/${action}`);
      toast.success(`User ${action}${action.endsWith("e") ? "d" : "ed"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async (val) => {
    await navigator.clipboard.writeText(val);
    toast.success("Copied");
  };

  const hardDelete = async () => {
    try {
      const { data: res } = await api.delete(`/admin/users/${id}`);
      toast.success(`Account deleted (${res.deleted_restaurants} outlets removed)`);
      navigate("/admin/users");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setOutletAccess = async (payload) => {
    try {
      await api.post(`/admin/users/${id}/set-outlet-access`, payload);
      toast.success("Outlet access updated");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const extendValidity = async ({ days }) => {
    try {
      const { data: res } = await api.post(`/admin/users/${id}/extend-subscription`, { days });
      toast.success(`Extended by ${days} days · new expiry ${new Date(res.new_ends_at).toLocaleDateString()}`);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  if (!data) {
    return <DashboardLayout role="super_admin"><div className="text-muted-foreground" data-testid="user-detail-loading">Loading…</div></DashboardLayout>;
  }

  const { user, restaurant, subscription, item_count } = data;

  return (
    <DashboardLayout role="super_admin">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-foreground/60 hover:text-foreground mb-6" data-testid="back-to-users">
        <ArrowLeft className="w-4 h-4" /> Back to all users
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">User profile</div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight" data-testid="user-detail-name">{user.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            {user.is_banned && <Badge variant="destructive">Banned</Badge>}
            {user.is_suspended && <Badge className="bg-amber-500 hover:bg-amber-500">Suspended</Badge>}
            {!user.is_banned && !user.is_suspended && <Badge className="bg-veg hover:bg-veg">Active</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setPwOpen(true)} className="bg-brand-navy hover:bg-[#1A2B42] text-white rounded-full" data-testid="open-change-password">
            <KeyRound className="w-4 h-4" /> Change password
          </Button>
          {user.role === "owner" && (
            <ManageAccessDialog
              email={user.email}
              subscription={subscription}
              outletCount={restaurant ? 1 : 0}
              onSave={setOutletAccess}
            />
          )}
          {user.is_banned ? (
            <Button variant="outline" onClick={() => act("unban")} data-testid="user-unban">
              <CheckCircle2 className="w-4 h-4" /> Unban
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => act("ban")} data-testid="user-ban">
              <Ban className="w-4 h-4" /> Ban
            </Button>
          )}
          {user.is_suspended ? (
            <Button variant="outline" onClick={() => act("unsuspend")} data-testid="user-unsuspend">
              <PlayCircle className="w-4 h-4" /> Resume
            </Button>
          ) : (
            <Button variant="outline" onClick={() => act("suspend")} data-testid="user-suspend">
              <PauseCircle className="w-4 h-4" /> Suspend
            </Button>
          )}
          {!PROTECTED_EMAILS.has((user.email || "").toLowerCase()) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="user-delete">
                  <Trash2 className="w-4 h-4" /> Delete account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Permanently delete {user.email}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    All of this owner&rsquo;s restaurants, menus, images, subscriptions, payments and activity logs
                    will be wiped. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={hardDelete} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="confirm-user-delete">
                    Delete account
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6 border border-border" data-testid="user-overview-card">
            <h3 className="font-heading font-semibold mb-2">Overview</h3>
            <InfoRow
              icon={Hash} label="User ID" value={user.id} mono
              action={<button onClick={() => copy(user.id)} className="p-1.5 hover:bg-muted rounded" title="Copy"><Copy className="w-3.5 h-3.5 text-foreground/40" /></button>}
            />
            <InfoRow icon={Mail} label="Email" value={user.email} />
            <InfoRow
              icon={CheckCircle2} label="Verification"
              value={<Badge className="bg-veg hover:bg-veg">Verified</Badge>}
            />
            <InfoRow icon={Calendar} label="Account created" value={fmtDate(user.created_at)} />
            <InfoRow icon={LogIn} label="Last login" value={fmtDate(user.last_login_at)} />
            <InfoRow icon={Globe} label="Last login IP" value={user.last_login_ip || "—"} mono />
          </Card>

          {restaurant && (
            <Card className="p-6 border border-border" data-testid="user-restaurant-card">
              <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><Store className="w-4 h-4" /> Restaurant</h3>
              <InfoRow icon={Store} label="Name" value={restaurant.name} />
              <InfoRow icon={Hash} label="Slug" value={restaurant.slug} mono />
              <InfoRow icon={Utensils} label="Menu items" value={item_count} />
              <InfoRow
                icon={CreditCard} label="Plan"
                value={subscription ? `${subscription.plan} · ${subscription.status}` : "—"}
              />
              <a href={`/m/${restaurant.slug}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="mt-3 w-full" data-testid="view-live-menu">
                  <ExternalLink className="w-4 h-4" /> View live menu
                </Button>
              </a>
            </Card>
          )}
        </div>

        <Card className="p-6 border border-border lg:col-span-2" data-testid="user-activity-card">
          <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Activity timeline</h3>
          {activity.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">No activity yet.</div>
          ) : (
            <ol className="relative border-l border-border ml-4 space-y-5">
              {activity.map((ev) => {
                const meta = eventMeta(ev.event_type);
                const Icon = meta.icon;
                return (
                  <li key={ev.id} className="ml-6" data-testid={`activity-${ev.event_type}`}>
                    <span className={`absolute -left-3.5 w-7 h-7 rounded-full ring-4 ring-white flex items-center justify-center ${meta.bg}`}>
                      <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    </span>
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <div className="font-semibold text-sm">{ev.message}</div>
                      <time className="text-xs text-foreground/50 whitespace-nowrap">{fmtDate(ev.created_at)}</time>
                    </div>
                    <div className="text-xs text-foreground/50 mt-1 font-mono">
                      {ev.event_type}{ev.ip ? ` · ${ev.ip}` : ""}
                    </div>
                    {ev.metadata && Object.keys(ev.metadata).length > 0 && (
                      <div className="mt-1.5 text-xs text-foreground/50">
                        {Object.entries(ev.metadata).slice(0, 3).map(([k, v]) => (
                          <span key={k} className="mr-3"><span className="uppercase tracking-[0.15em] font-bold">{k}</span>: {String(v)}</span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      </div>

      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} user={user} onSuccess={load} />
    </DashboardLayout>
  );
}
